import { h } from 'preact';
import GoogleMapsLoader, { google } from 'google-maps';
import { GOOGLE_MAP_API_KEY, PROPERTY_PAGE_MARKER_ICON, DEFAULT_MAP_LATITUDE, DEFAULT_MAP_LONGITUDE } from '../../../../constants/maps';

import { HopplerModal, HopplerModalProps } from '../common/hoppler-modal-component';
import { REGIONS } from '../../../../constants/regions';
import { LocationSearchBox } from '../common/location-search.component';
import { DivCat, Location } from '../../../../types';
import { PropertyUtils } from '../../../../core/property-utils';

interface LocationRequestModalProps extends HopplerModalProps {
  divCat: DivCat;
  consumerMode: boolean;

  onLocationDone?: (location: Location) => any;
};

interface LocationRequestModalState {
  region?: string;
  city?: string;
  area?: string;
  building?: string;

  map?: google.maps.Map;
  selectedLat?: number;
  selectedLon?: number;

  typeDescription?: 'Building' | 'Area';
};

const BLDG_RES_AMENITIES = [
  { label: 'Function Room', field: 'functionRoom', readOnlyIfInBuilding: true },
  { label: 'Gym', field: 'gym', readOnlyIfInBuilding: true },
  { label: 'Pet Friendly', field: 'petFriendly' },
  { label: 'Playground', field: 'playground' },
  { label: 'Security', field: 'security', readOnlyIfInBuilding: true },
  { label: 'Swimming Pool', field: 'swimmingPool', readOnlyIfInBuilding: true }
];

const BLDG_COM_AMENITIES = [
  { label: 'LEED', field: 'leed' },
  { label: 'PEZA Accredited', field: 'pezaAccredited' }
];

/**
 * Implements the "Request New Location" dialog. This code is heavily patterned
 * after the AddLocationDialogController from Kumita 1.0 (frontendv2).
 */
export class LocationRequestModal extends HopplerModal<LocationRequestModalProps, LocationRequestModalState> {
  propertyUtils = new PropertyUtils();

  constructor(props: LocationRequestModalProps) {
    super(props);

    if (this.propertyUtils.doesTypeRequireBuilding(props.divCat)) {
      this.state = { typeDescription: 'Building' };
    } else {
      this.state = { typeDescription: 'Area' };
    }
  }

  doModalClose() {

  }

  isSimplifiedBuildingInfoRequired() {
    return (
      this.props.consumerMode &&
      this.state.typeDescription == 'Building'
    );
  }

  isFormComplete(props: LocationRequestModalProps, state: LocationRequestModalState) {
    let valid: boolean;

    valid = !!(state.region && state.city && state.area);
    if (state.typeDescription === 'Building') {
      valid = valid && !!(state.building && state.selectedLat && state.selectedLon);
    }

    return valid;
  }

  onCitySelect(location: Location) {
    this.setState({
      region: location.region,
      city: location.city
    });
  }

  onCityInput(cityName: string) {
    this.setState({ city: cityName });
  }

  onAreaSelect(location: Location) {
    this.setState({
      region: location.region,
      city: location.city,
      area: location.area
    });
  }

  onAreaInput(areaName: string) {
    this.setState({ area: areaName });
  }

  onBuildingNameChange(event: Event) {
    const elem = event.target as HTMLInputElement;
    this.setState({ building: elem.value });
  }

  onComplete() {
    // The duplicate checking code wants a `name` field
    const name = this.state.building || this.state.area;

    if (this.props.onLocationDone) {
      this.props.onLocationDone({
        region: this.state.region,
        city: this.state.city,
        area: this.state.area,
        building: this.state.building,
        latitude: this.state.selectedLat,
        longitude: this.state.selectedLon,
        
        name,
        label: name,
        newLocation: true
      });
    }
    this.props.close();
  }

  loadMap() {
    GoogleMapsLoader.VERSION = 'weekly';
    GoogleMapsLoader.KEY = GOOGLE_MAP_API_KEY;

    GoogleMapsLoader.load(async (google) => {
      const map = new google.maps.Map(
        document.getElementById('hiLocationRequestModalMap'),
        {
          center: new google.maps.LatLng(DEFAULT_MAP_LATITUDE, DEFAULT_MAP_LONGITUDE),
          zoom: 14
        }
      );

      this.setState({ map });
      const marker = new google.maps.Marker({
        position: {
          lat: DEFAULT_MAP_LATITUDE, 
          lng: DEFAULT_MAP_LONGITUDE
        },
        opacity: 1,
        icon: PROPERTY_PAGE_MARKER_ICON,
        draggable: true
      });
      google.maps.event.addListener(marker, 'dragend', (evt) => {
        this.setState({
          selectedLat: evt.latLng.lat(),
          selectedLon: evt.latLng.lng()
        });
      });

      marker.setMap(map);
    });
  }

  getAreaSection(props: LocationRequestModalProps, state: LocationRequestModalState) {
    let inputWidget: JSX.Element;

    if (state.typeDescription === 'Building') {
      inputWidget = <LocationSearchBox divCat={ props.divCat }
        level='area'
        onLocationSelected={ location => this.onAreaSelect(location) }
        onInput={ area => this.onAreaInput(area) }
        initialText={ state.area }
      />;
    } else {
      inputWidget = (
        <input type="text"
          className="form-control"
          value={ state.area }
          onInput={ e => this.onAreaInput((e.target as HTMLInputElement).value) }/>
      )
    }
    return (
      <div className="form-group">
        <label className="font-weight-bold">Area Name</label>
        { inputWidget }
      </div>
    );
  }

  getSimplifiedBuildingSection(props: LocationRequestModalProps, state: LocationRequestModalState) {
    if (this.isSimplifiedBuildingInfoRequired()) {
      return (
        <div>
          <div className="form-group">
            <label className="font-weight-bold">Building Name</label>
            <input type="text" className="form-control"
              value={ state.building }
              onChange={ event => this.onBuildingNameChange(event) } />
          </div>

          <div className="d-flex flex-column">
            <label className="font-weight-bold">Where is this building located?</label>
            <span>Drag the orange pin to the building's exact location.</span>
          </div>

          <div className="hi-location-request__map-container" id="hiLocationRequestModalMap"></div>
        </div>
      );
    }
  }

  render(props: LocationRequestModalProps, state: LocationRequestModalState) {
    return this.modalize(
      <div className="hi-location-request">
        <div className="hi-location-request__header">
          <h3 className="hi-location-request__heading">Request new location</h3>
        </div>
        <form className="hi-location-request__body">
          <div className="form-group">
            <label className="font-weight-bold">Region</label>
            <select className="form-control" placeholder="Select a Region" value={ state.region }>
              { REGIONS.map(region => (
                <option value={ region.name }>
                  { region.name }
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="font-weight-bold">Province/City</label>
            <LocationSearchBox divCat={ props.divCat }
              level='city'
              onLocationSelected={ location => this.onCitySelect(location) }
              onInput={ city => this.onCityInput(city) }
              initialText={ state.city }
            />
          </div>
          { this.getAreaSection(props, state) }

          { this.getSimplifiedBuildingSection(props, state) }
        </form>
        <div className="hi-location-request__footer">
          <button className="btn" onClick={ () => this.props.close() }>
            Cancel
          </button>
          <button className="btn btn-secondary" onClick={ () => this.onComplete() }
            disabled={ !this.isFormComplete(props, state) }>
            OK
          </button>
        </div>
      </div>
    )
  }

  componentDidMount() {
    this.loadMap();
  }
}
